// Height resolvers for 3D extrusion (Fase 0 of the 3D preview).
//
// The 2D model stays the source of truth; these helpers derive the vertical
// dimensions the extrusion needs, honouring configurable catalog values and
// falling back to factory defaults. No rendering here — pure data.

import type {
  BenchTier,
  GlassSegment,
  ProductInstance,
  ProductTemplate,
  SaunaDoor,
} from '../shared/types';
import { wallThicknessOf } from './dent';
import { defaultSaunaHeight } from './saunaLayout';
import { hotTubModelOf } from './hotTub';
import { traitsOf } from '../shared/categories';
import {
  isTilted,
  tiltedExtents,
} from './tilt';

export type BenchTierLevel = 'low' | 'medium' | 'high';

/** Factory bench seat heights (mm), used when a product doesn't configure its
 *  own. The product catalog value (`template.benchTierHeights`) overrides these,
 *  so nothing user-facing is hardcoded — this is only the fallback. */
export const DEFAULT_BENCH_TIER_HEIGHTS: Record<BenchTierLevel, number> = {
  low: 450,
  medium: 900,
  high: 1200,
};

export const DEFAULT_WALL_HEIGHT = 2700;

/** Extruded height (mm) of a user-drawn wall. */
export function wallHeight(wall: { height?: number }): number {
  return wall.height ?? DEFAULT_WALL_HEIGHT;
}

/** Height (mm) of a wall's underside above the floor in the 3D preview. May be
 *  negative to sink the wall below the floor reference. */
export function wallBase(wall: { heightFromGround?: number }): number {
  return wall.heightFromGround ?? 0;
}

/** 3D extrusion height (mm) of a user-drawn polygon/ellipse; 0 = flat (not
 *  extruded in the 3D preview). */
export function polygonHeight(poly: { height?: number }): number {
  return Math.max(0, poly.height ?? 0);
}

/** Height (mm) of a polygon's underside above the floor in the 3D preview. May be
 *  negative to sink the shape below the floor reference. */
export function polygonBase(poly: { heightFromGround?: number }): number {
  return poly.heightFromGround ?? 0;
}

/** The three tier heights (mm) for a product, catalog value over factory default. */
export function benchTierHeights(
  template?: Pick<ProductTemplate, 'benchTierHeights'> | null,
): Record<BenchTierLevel, number> {
  const t = template?.benchTierHeights;
  return {
    low: t?.low ?? DEFAULT_BENCH_TIER_HEIGHTS.low,
    medium: t?.medium ?? DEFAULT_BENCH_TIER_HEIGHTS.medium,
    high: t?.high ?? DEFAULT_BENCH_TIER_HEIGHTS.high,
  };
}

/** Seat height (mm) of one bench: explicit per-bench override, else the product's
 *  configured height for the bench's tier, else the factory default. */
export function benchHeight(
  bench: Pick<BenchTier, 'tier' | 'height'>,
  template?: Pick<ProductTemplate, 'benchTierHeights'> | null,
): number {
  if (typeof bench.height === 'number') return bench.height;
  return benchTierHeights(template)[bench.tier ?? 'low'];
}

/** Seat-slab thickness (mm) of a bench: the thickest of its per-slat thicknesses.
 *  Falls back to the legacy per-bench `thickness` for drawings saved before slat
 *  thickness existed. Returns undefined when the bench has no thickness (solid to
 *  the floor). */
export function benchThickness(bench: Pick<BenchTier, 'slatThickness' | 'thickness'>): number | undefined {
  const st = bench.slatThickness?.filter((t) => typeof t === 'number' && t > 0);
  if (st && st.length) return Math.max(...st);
  return typeof bench.thickness === 'number' && bench.thickness > 0 ? bench.thickness : undefined;
}

/** Default slat thickness (mm): a new slat, a preset that predates thickness, and
 *  the section / 3D of a slatted bench that carries none. */
export const DEFAULT_SLAT_THICKNESS = 28;

/** Underside (mm) of slat `i` of a slatted bench whose seat top is `top`: the slat
 *  hangs its own thickness from the top — else the bench's ({@link benchThickness}),
 *  else {@link DEFAULT_SLAT_THICKNESS} — clamped to the floor. A slatted bench is
 *  never solid to the floor: the gaps between slats are open all the way down. */
export function slatBase(bench: Pick<BenchTier, 'slatThickness' | 'thickness'>, i: number, top: number): number {
  const own = bench.slatThickness?.[i];
  const t = typeof own === 'number' && own > 0 ? own : benchThickness(bench) ?? DEFAULT_SLAT_THICKNESS;
  return Math.max(0, top - t);
}

/** Underside height (mm) of one bench's fill above the floor. Without a thickness
 *  the bench is solid to the floor (base 0). With one (the thickest slat) the seat
 *  is a slab hanging from the top surface, so the base sits at `height - thickness`
 *  (clamped to the floor), leaving a void below. */
export function benchBase(
  bench: Pick<BenchTier, 'tier' | 'height' | 'slatThickness' | 'thickness'>,
  template?: Pick<ProductTemplate, 'benchTierHeights'> | null,
): number {
  const thick = benchThickness(bench);
  if (thick == null) return 0;
  return Math.max(0, benchHeight(bench, template) - Math.max(0, thick));
}

/** A room's overall (external) height in mm — the outer top of its ceiling: the
 *  stored `height`, else the default by depth (2100 / 2300). The ONE resolver of
 *  the stored figure: read it, never `inst.height ?? defaultSaunaHeight(…)`. */
export function externalHeight(inst: ProductInstance): number {
  return inst.height ?? defaultSaunaHeight(inst.depth);
}

/** A room ceiling's OWN thickness in mm (a finite figure, never below 0), or
 *  null while it follows the walls. The ONE reader of `ceiling.thickness`: the
 *  panel shows it, `ceilingThicknessOf` falls back from it. */
export function ownCeilingThickness(inst: ProductInstance): number | null {
  const t = inst.ceiling?.thickness;
  return typeof t === 'number' && Number.isFinite(t) ? Math.max(0, t) : null;
}

/** A room ceiling's thickness in mm: its own, else the room's wall thickness.
 *  0 = no ceiling (never drawn). */
export function ceilingThicknessOf(inst: ProductInstance): number {
  return ownCeilingThickness(inst) ?? Math.max(0, wallThicknessOf(inst));
}

/** The FLAT ceiling's underside in mm, i.e. where the walls stop: external
 *  minus the ceiling thickness. There is no floor, so nothing is subtracted at
 *  the bottom. */
export function interiorHeight(inst: ProductInstance): number {
  return Math.max(0, externalHeight(inst) - ceilingThicknessOf(inst));
}

/** An interior partition runs floor to ceiling unless it says otherwise; both
 *  values are measured from the room's own floor, like a bench's. */
export function innerWallBase(iw: { heightFromGround?: number }): number {
  return Math.max(0, iw.heightFromGround ?? 0);
}

/** A partition's top under a FLAT ceiling: one with no height of its own reaches
 *  the underside wherever it starts, one with its own height stands that tall. */
export function innerWallTop(inst: ProductInstance, iw: { height?: number; heightFromGround?: number }): number {
  const base = innerWallBase(iw);
  return iw.height == null ? Math.max(base, interiorHeight(inst)) : base + Math.max(0, iw.height);
}

/** A partition's top under a ceiling whose underside is at `under` there: one
 *  with no height of its own follows the underside, one with its own height is
 *  clipped by it. */
export function innerWallTopAt(iw: { height?: number; heightFromGround?: number }, under: number): number {
  return iw.height == null ? under : Math.min(innerWallBase(iw) + iw.height, under);
}

/** A hidden heater wall's underside and top (mm above the room's floor): it
 *  floats from its height from ground, its own height tall. */
export function heaterWallBase(hw: { heightFromGround: number }): number {
  return Math.max(0, hw.heightFromGround);
}
export function heaterWallTop(hw: { height: number; heightFromGround: number }): number {
  return heaterWallBase(hw) + Math.max(0, hw.height);
}

/** Height (mm) of a room's floor above ground level, lifting the whole room in
 *  3D and in sections (like a heater's / wall's height-from-ground). May be
 *  negative to sink the room below the floor reference. */
export function roomBase(inst: Pick<ProductInstance, 'heightFromGround'>): number {
  return inst.heightFromGround ?? 0;
}

/** Overall height in mm of a node/product object (salt wall, heated bench, …):
 *  its explicit `height`, else its category's `defaultHeight`. A hot tub's is its
 *  MODEL's, not a category default: a Lodge+ is 90 cm and a Virtus 93, and every
 *  figure in the layout (the waterline, the seats, the coping) is a proportion of it. */
export function objectHeight(inst: ProductInstance): number {
  const d = traitsOf(inst.category).defaultHeight;
  return inst.height ?? (d === 'model' ? hotTubModelOf(inst.hotTubModel).ref.h : d);
}

/** A node object's Height written (a salt wall's, a standalone sauna or heated
 *  bench's): whole mm, at least 1 — its panel row's and its handle's one setter. */
export function setObjectHeight(inst: ProductInstance, v: number): void {
  inst.height = Math.max(1, Math.round(v));
}

export const DEFAULT_GLASS_HEIGHT = 2000;
export const DEFAULT_SILL_HEIGHT = 1000;
export const DEFAULT_HEATER_HEIGHT = 750;
/** Default height (mm) of a heater body's underside above the floor. */
export const DEFAULT_HEATER_HEIGHT_FROM_GROUND = 150;

/** A glazed pane's top (mm above the floor): its own height, else the room's
 *  glass height, else 2000. The ONE resolver — plan, section and 3D read it, so a
 *  pane given its own height is that height everywhere. */
export function glassPaneTop(inst: Pick<ProductInstance, 'glassHeight'>, seg: Pick<GlassSegment, 'height'>): number {
  return seg.height ?? inst.glassHeight ?? DEFAULT_GLASS_HEIGHT;
}

/** A doorway's top (mm above the floor): the door's own height, else the room's
 *  glass height, else 2000. The ONE resolver — section, 3D and the cut note read
 *  it (a door's glass segment carries the door's height, so `glassPaneTop` of it
 *  is this figure). */
export function doorTop(inst: Pick<ProductInstance, 'glassHeight'>, door: Pick<SaunaDoor, 'height'>): number {
  return door.height ?? inst.glassHeight ?? DEFAULT_GLASS_HEIGHT;
}

/** A glazed pane's base (mm above the floor): below it the wall is solid, the
 *  glazing spans from here up to `glassPaneTop`. Absent / 0 = glazed from the
 *  floor. The ONE resolver — section and 3D read it, never `seg.heightFromGround`
 *  raw, so a pane sits at one height everywhere. */
export function glassPaneBase(seg: Pick<GlassSegment, 'heightFromGround'>): number {
  return Math.max(0, seg.heightFromGround ?? 0);
}

/** A drawing saved before every object had the one height from ground: a part's
 *  `heaterHeightFromGround` and an opening's `sillHeight` become their
 *  `heightFromGround` (a room never read the first: there it is dropped). */
export function migrateHeightFromGround(inst: ProductInstance): void {
  const old = inst as ProductInstance & { heaterHeightFromGround?: unknown };
  if ('heaterHeightFromGround' in old) {
    if (!inst.autoBenches && inst.heightFromGround == null && typeof old.heaterHeightFromGround === 'number') inst.heightFromGround = old.heaterHeightFromGround;
    delete old.heaterHeightFromGround;
  }
  for (const seg of inst.openings ?? []) {
    const o = seg as GlassSegment & { sillHeight?: unknown };
    if (!('sillHeight' in o)) continue;
    if (o.heightFromGround == null && typeof o.sillHeight === 'number') o.heightFromGround = o.sillHeight;
    delete o.sillHeight;
  }
}

/** An opening's sill (mm): its height from ground — below it the wall is solid. */
export function openingSill(seg: Pick<GlassSegment, 'heightFromGround'>): number {
  return Math.max(0, seg.heightFromGround ?? DEFAULT_SILL_HEIGHT);
}

/** An opening's architrave height (mm): above it the wall is solid; defaults to
 *  the room's glass height. */
export function openingArchitrave(inst: ProductInstance, seg: GlassSegment): number {
  return seg.architraveHeight ?? inst.glassHeight ?? DEFAULT_GLASS_HEIGHT;
}

/** Heater body height (mm) from the catalog, default 750. */
export function heaterHeight(template?: Pick<ProductTemplate, 'heaterHeight'> | null): number {
  return template?.heaterHeight ?? DEFAULT_HEATER_HEIGHT;
}

/** Height (mm) of a heater body's underside above the floor: the part's own
 *  height from ground, else the catalog default, else 150. */
export function heaterHeightFromGround(
  inst: Pick<ProductInstance, 'heightFromGround'>,
  template?: Pick<ProductTemplate, 'heightFromGround'> | null,
): number {
  return inst.heightFromGround ?? template?.heightFromGround ?? DEFAULT_HEATER_HEIGHT_FROM_GROUND;
}

/** Where a placed copy of a catalogue product (or of one of its parts) starts:
 *  the catalogue's Height from ground, else 150 for a category `liftedByDefault`;
 *  undefined for any other (the floor). */
export function catalogBase(value: number | undefined, category: string): number | undefined {
  return value ?? (traitsOf(category).liftedByDefault ? DEFAULT_HEATER_HEIGHT_FROM_GROUND : undefined);
}

/** The catalogue figures a part falls back to. */
export type PartFigures = Pick<ProductTemplate, 'heaterHeight' | 'heightFromGround'>;

/** A part's underside and top (mm above the ground) — the ONE resolver the 3D
 *  view, the sections, the selection's stacking, the panel and its handle read: a
 *  heater's or a shower's body from its Height from ground, its Height tall (the
 *  part's own, else its catalogue product's — `template`, which main.ts and the
 *  3D view pass; a section reads the part alone, a placed heater or shower
 *  carrying both —, else 150 and 750); anything else from its own Height from
 *  ground when it has one (a part sunk into a pit), else the floor, `objectHeight`
 *  tall. */
export function partSpan(inst: ProductInstance, template?: PartFigures | null): [number, number] {
  const base = traitsOf(inst.category).floatsBody ? heaterHeightFromGround(inst, template) : inst.heightFromGround ?? 0;
  const h = partHeight(inst, template);
  // A tilted body stands its tilted height, its lowest point at its base.
  return [base, base + (isTilted(inst) ? tiltedExtents(inst, { w: inst.width, d: inst.depth, h }).hz : h)];
}

/** A part's own height, upright: a heater's or a shower's body's Height (its
 *  own, else its catalogue product's, else 750); anything else's `objectHeight`.
 *  Tilted, it stands `partSpan` tall instead. */
export function partHeight(inst: ProductInstance, template?: PartFigures | null): number {
  return traitsOf(inst.category).floatsBody ? Math.max(0, inst.height ?? heaterHeight(template)) : objectHeight(inst);
}
